/**
 * The pilot's product health, as the four questions it answers.
 *
 * `GET /internal/landing-stats` returns eleven figures and had no consumer
 * anywhere in the repo. Printed as eleven tiles they would answer nothing: the
 * point of `dau` beside `mau` is the ratio between them, and the point of
 * `approval_coverage` and `failure_rate` is the distance between where they are
 * and where they ought to be. So both are drawn - a ratio as a share of its own
 * denominator, a proportion against a visible target - and the digits stay
 * beside the shape rather than instead of it.
 *
 * This is a screen rather than a band on the operations overview. The overview
 * answers whether the product is up, what is queued and what has failed; how
 * much the product is used and whether its output is any good is a different
 * question, and stacking the two made one page of fourteen tiles that answered
 * neither at a glance.
 *
 * The targets drawn here are definitional, not agreed service levels: every
 * outfit reviewed is 100% coverage and no job failing is a 0% failure rate.
 * Nobody has set an SLO for either, and the screen says so rather than letting
 * a tick mark imply one.
 *
 * Charts are plain SVG with an HTML overlay, the approach `charts.tsx` takes,
 * for the same reason: a two-value meter is less
 * code drawn than configured.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { landingStatsApiV1InternalLandingStatsGet } from '@closet-theory/api';
import type { AdminLandingStats } from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Section, StatTile } from '@ui/console/primitives';
import { MetricBand } from '@ui/console/MetricBand';
import { Term } from '@ui/console/glossary';
import { Bar, Quantity, formatCount, formatPercent, plural } from '@ui/console/measures';
import type { Tone } from '@ui/console/status';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Page } from './components';
import { TONE_VARIABLE } from './charts';

/**
 * How much of the month's audience came back today. A ratio, not a count: the
 * two figures it is built from are already on the page beside it.
 *
 * `null` rather than zero when nobody used the product in the last 30 days -
 * there is no audience to take a share of, which is a different fact from
 * "nobody came back".
 */
export function stickiness(stats: Pick<AdminLandingStats, 'dau' | 'mau'>): number | null {
  return stats.mau > 0 ? stats.dau / stats.mau : null;
}

/** A percentage with its exact fraction one hover away, or an em dash. */
function Percent({ fraction }: { fraction: number | null }) {
  if (fraction === null) return <span className="text-tertiary">not enough data</span>;
  return <Quantity value={formatPercent(fraction)} raw={fraction} />;
}

/**
 * One figure at three windows, in one tile.
 *
 * DAU, WAU and MAU had a tile each, so a pilot nobody had signed into yet spent
 * three quarters of the row saying `0 members` three times. They are one
 * measurement - distinct members with a sign-in session - read over one day,
 * seven days and thirty, and they are only worth anything against each other.
 */
function SignInActivity({ stats }: { stats: AdminLandingStats }) {
  const share = stickiness(stats);
  const windows: Array<[string, number, string]> = [
    ['Today', stats.dau, 'Distinct members with a sign-in session in the last 24 hours.'],
    ['Last 7 days', stats.wau, 'Distinct members with a sign-in session in the last 7 days.'],
    ['Last 30 days', stats.mau, 'Distinct members with a sign-in session in the last 30 days.'],
  ];
  return (
    <figure className="m-0 flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <figcaption className="text-xs font-medium text-tertiary uppercase">
        <Term
          term="Members signing in"
          definition="Distinct people with a sign-in session in the window. This counts sessions, not generations: a member we produced outfits for who never signed in is not here."
        />
      </figcaption>
      <dl className="grid grid-cols-3 gap-3">
        {windows.map(([label, value, meaning]) => (
          <div key={label} className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-xs text-tertiary" title={meaning}>
              {label}
            </dt>
            <dd className="m-0 text-display-xs font-semibold break-words text-primary">
              <Quantity value={formatCount(value)} unit={plural(value, 'member')} raw={value} />
            </dd>
          </div>
        ))}
      </dl>
      {share === null ? (
        <p className="text-xs text-tertiary">
          <Term
            term="Stickiness (DAU ÷ MAU)"
            definition="Of everyone who signed in at all this month, the share who signed in today. It is a ratio between two of the counts above, not a count of its own."
          />
          : nobody signed in during the last 30 days, so there is no audience to take a share of.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          <p className="flex flex-wrap items-baseline justify-between gap-2 text-xs text-tertiary">
            <span>
              <Term
                term="Stickiness (DAU ÷ MAU)"
                definition="Of everyone who signed in at all this month, the share who signed in today. It is a ratio between two of the counts above, not a count of its own."
              />
            </span>
            <span className="font-semibold text-primary">
              <Percent fraction={share} />
            </span>
          </p>
          <Bar
            fraction={share}
            tone="info"
            label={`Members active today as a share of members active this month: ${formatCount(stats.dau)} of ${formatCount(stats.mau)}`}
          />
          <p className="text-xs text-tertiary">
            {formatCount(stats.dau)} of {formatCount(stats.mau)} members who signed in this month
            came back today.
          </p>
        </div>
      )}
    </figure>
  );
}

/**
 * A proportion drawn against the value it is aiming at.
 *
 * `direction` says which side of the target is good, because the same picture
 * means opposite things for coverage (reach the target) and for a failure rate
 * (stay under it).
 *
 * With no proportion to draw it collapses to a single line. An absent figure
 * used to occupy a full card to say `not enough data`, which gave the thing we
 * could not measure the same weight on screen as the things we could.
 */
export function TargetMeter({
  label,
  definition,
  fraction,
  target,
  targetLabel,
  direction,
  detail,
  absent,
}: {
  /** The precise term, shown verbatim and explained in place. */
  label: string;
  definition: string;
  /** The measured proportion, 0 to 1, or `null` when it cannot be computed. */
  fraction: number | null;
  /** Where it ought to be, 0 to 1. */
  target: number;
  targetLabel: string;
  direction: 'at-or-above' | 'at-or-below';
  /** The counts the proportion was taken over, in words. */
  detail: ReactNode;
  /** Why there is no proportion, shown instead of the bar when there is none. */
  absent: string;
}) {
  if (fraction === null) {
    return (
      <p className="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-xl border border-secondary bg-primary px-4 py-2.5 text-xs text-tertiary">
        <span className="font-medium text-secondary uppercase">
          <Term term={label} definition={definition} />
        </span>
        <span className="text-tertiary">not enough data</span>
        <span className="min-w-0">{absent}</span>
      </p>
    );
  }
  const met = direction === 'at-or-above' ? fraction >= target : fraction <= target;
  const tone: Tone = met ? 'success' : 'warning';
  const percent = Math.min(100, Math.max(0, fraction * 100));
  return (
    <figure className="m-0 flex flex-col gap-2 rounded-xl border border-secondary bg-primary p-4">
      <figcaption className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-tertiary uppercase">
          <Term term={label} definition={definition} />
        </span>
        <span className="text-display-xs font-semibold text-primary">
          <Percent fraction={fraction} />
        </span>
      </figcaption>
      <div className="relative">
        <svg
          className="h-3 w-full"
          viewBox="0 0 100 12"
          preserveAspectRatio="none"
          role="img"
          aria-label={`${label}: ${formatPercent(fraction)} against a target of ${formatPercent(target)} ${direction === 'at-or-above' ? 'or higher' : 'or lower'}.`}
        >
          <rect x="0" y="0" width="100" height="12" fill="var(--color-bg-secondary)" rx="0" />
          <rect x="0" y="0" width={percent} height="12" fill={TONE_VARIABLE[tone]} />
          <rect
            x={Math.min(99.4, Math.max(0, target * 100 - 0.3))}
            y="0"
            width="0.6"
            height="12"
            fill="var(--color-fg-primary, currentColor)"
          />
        </svg>
        {/* The tick's meaning in words, over the drawing rather than in it:
            the accessible name on the svg already carries it for a reader
            who cannot see where the tick sits. */}
        <span
          className="pointer-events-none absolute -bottom-4 -translate-x-1/2 text-xs whitespace-nowrap text-quaternary tabular-nums"
          style={{ left: `${Math.min(92, Math.max(8, target * 100))}%` }}
        >
          {targetLabel}
        </span>
      </div>
      <p className="pt-4 text-xs text-tertiary">{detail}</p>
    </figure>
  );
}

/** What each read can be in, so the section never renders a stale figure silently. */
export type PilotHealthState = 'loading' | 'ready' | 'failed';

const DAILY_LOOKS_TOOLTIP =
  'Counted over daily look slots dated in the window, through today on the IST clock, that have reached stylist review. A slot still waiting to be generated is not counted.';

/**
 * The section itself. It owns its own read rather than joining any dashboard's
 * `Promise.allSettled`, so a landing-stats outage leaves whatever is around it
 * - which answers a different question - completely intact.
 */
export function PilotHealth({
  windowDays = 7,
  onLoaded,
}: {
  windowDays?: number;
  /**
   * Hands the stats up so the page above can draw its band from them. The read
   * itself stays here: a landing-stats outage must not take anything else on
   * the screen with it, which is only true while one component owns the call.
   */
  onLoaded?: (stats: AdminLandingStats) => void;
}) {
  const [stats, setStats] = useState<AdminLandingStats>();
  const [state, setState] = useState<PilotHealthState>('loading');
  const load = useCallback(async () => {
    configureAdminApi();
    setState('loading');
    // A thrown transport error and a 500 mean the same thing to this section,
    // and neither may take the rest of the screen down with it.
    const result = await landingStatsApiV1InternalLandingStatsGet({
      query: { window_days: windowDays },
    }).catch(() => undefined);
    if (!result) {
      setState('failed');
      return;
    }
    handleUnauthorized(result.response.status);
    if (result.error || !result.data) {
      setState('failed');
      return;
    }
    setStats(result.data);
    setState('ready');
    onLoaded?.(result.data);
  }, [windowDays, onLoaded]);
  useEffect(() => void load(), [load]);

  const window = stats?.window_days ?? windowDays;
  const unreviewed = stats?.day_ahead_slots_shipped_unactioned_in_window ?? 0;
  return (
    <Section
      title="Is the pilot being used, and is what it produces any good?"
      description={
        // The window used to be a tile of its own, which made a fact about how
        // the section is counted look like something the section measured.
        stats
          ? `Counted over the trailing ${formatCount(window)} ${plural(window, 'day')}, as of ${new Date(stats.as_of).toLocaleString()}. The sign-in counts keep their own 1, 7 and 30 day windows.`
          : `Counted over the trailing ${formatCount(window)} ${plural(window, 'day')}.`
      }
      actions={
        <Button
          size="sm"
          color="secondary"
          isDisabled={state === 'loading'}
          onClick={() => void load()}
        >
          {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      {state === 'failed' ? (
        <Callout
          tone="error"
          title="The pilot health read did not answer, so nothing below it is shown."
        >
          Every other figure on this page came from a different read and is unaffected. Nothing was
          changed by this; try again when the API is answering.
        </Callout>
      ) : null}
      {stats ? (
        <>
          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
            <SignInActivity stats={stats} />
            <StatTile
              label={
                <Term
                  term="Generations per generating member per day"
                  definition="Counted over members who claimed a generation in this window, which is a different population from the sign-in counts beside it: a member can sign in without generating, and we can generate for a member who never signed in."
                />
              }
              value={
                <Quantity
                  value={stats.generations_per_active_user_per_day.toFixed(2)}
                  unit="per generating member per day"
                  raw={stats.generations_per_active_user_per_day}
                />
              }
              hint="How much the product is asked for by the people asking for it. Its denominator is members who claimed a generation, not members who signed in, so it does not move with the sign-in counts."
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-2">
            <TargetMeter
              label="Approval coverage"
              definition="The share of daily looks due in this window that a stylist approved. It says how much of what we produced a human has actually looked at. A look nobody reviews still ships at the 11 PM IST cutoff."
              fraction={stats.approval_coverage}
              target={1}
              targetLabel="Target: 100% reviewed"
              direction="at-or-above"
              detail={
                <>
                  <Quantity
                    value={formatCount(stats.day_ahead_slots_approved_in_window)}
                    unit="looks approved"
                    raw={stats.day_ahead_slots_approved_in_window}
                  />{' '}
                  of{' '}
                  <Quantity
                    value={formatCount(stats.day_ahead_slots_in_window)}
                    unit="looks due"
                    raw={stats.day_ahead_slots_in_window}
                  />{' '}
                  in the last {formatCount(window)} days. The 100% mark is what the figure means at
                  its best, not a service level anyone has agreed to.
                </>
              }
              absent={`No daily look was due in the last ${formatCount(window)} days, so there is nothing whose reviewed share could be measured. The read succeeded. The 100% mark would be definitional, not a service level anyone has agreed to.`}
            />
            <TargetMeter
              label="Failure rate"
              definition="The share of generation jobs in this window that ended in failure. A ceiling, not a goal: every point of it is a member who asked for something and did not get it."
              fraction={stats.failure_rate}
              target={0}
              targetLabel="Target: 0% failing"
              direction="at-or-below"
              detail={
                <>
                  <Quantity
                    value={formatCount(stats.generation_jobs_failed_in_window)}
                    unit="jobs failed"
                    raw={stats.generation_jobs_failed_in_window}
                  />{' '}
                  of{' '}
                  <Quantity
                    value={formatCount(stats.generation_jobs_in_window)}
                    unit="jobs run"
                    raw={stats.generation_jobs_in_window}
                  />{' '}
                  in the last {formatCount(window)} days. The 0% mark is the definitional floor, not
                  a service level anyone has agreed to.
                </>
              }
              absent={`No generation job ran in the last ${formatCount(window)} days, so there is no denominator to take a failure share of. The read succeeded. The 0% mark would be definitional, not a service level anyone has agreed to.`}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatTile
              label="Daily looks due in the window"
              value={
                <Quantity
                  value={formatCount(stats.day_ahead_slots_in_window)}
                  unit={plural(stats.day_ahead_slots_in_window, 'look')}
                  raw={stats.day_ahead_slots_in_window}
                />
              }
              tooltip={DAILY_LOOKS_TOOLTIP}
              hint={`Daily looks made for a day in the last ${formatCount(window)} days, reviewed or not.`}
            />
            <StatTile
              label="Shipped unreviewed"
              value={
                <Quantity
                  value={formatCount(unreviewed)}
                  unit={plural(unreviewed, 'look')}
                  raw={unreviewed}
                />
              }
              {...(unreviewed > 0 ? { tone: 'warning' as const, statusLabel: 'not reviewed' } : {})}
              hint={`Daily looks that reached the member at the 11 PM IST cutoff without a stylist approving them, in the last ${formatCount(window)} days.`}
            />
            <StatTile
              label="Generation jobs run"
              value={
                <Quantity
                  value={formatCount(stats.generation_jobs_in_window)}
                  unit={plural(stats.generation_jobs_in_window, 'job')}
                  raw={stats.generation_jobs_in_window}
                />
              }
              hint={`${formatCount(stats.generation_jobs_failed_in_window)} of them failed. A job is one attempt at producing something, which is not one outfit — and not every job produces an outfit at all.`}
            />
          </div>
        </>
      ) : null}
    </Section>
  );
}

/** The screen, so the overview can go back to answering one question. */
export function ProductMetrics() {
  const [stats, setStats] = useState<AdminLandingStats>();
  const onLoaded = useCallback((next: AdminLandingStats) => setStats(next), []);
  const share = stats ? stickiness(stats) : null;
  const jobs = stats?.generation_jobs_in_window ?? 0;
  const failed = stats?.generation_jobs_failed_in_window ?? 0;
  return (
    <Page
      title="Product metrics"
      purpose="Use this page to answer how much the pilot is being used and whether what it produces is any good. Every figure is a live read; nothing here changes anything."
      band={
        <MetricBand
          label="Use, and whether what it produces is any good"
          figures={[
            {
              id: 'dau',
              label: 'Members signing in today',
              ...(stats
                ? {
                    value: formatCount(stats.dau),
                    unit: plural(stats.dau, 'member'),
                    raw: stats.dau,
                    ...(share === null
                      ? {}
                      : {
                          bar: {
                            fraction: share,
                            label: `Members active today as a share of members active this month: ${stats.dau} of ${stats.mau}`,
                          },
                        }),
                    hint:
                      share === null
                        ? 'Nobody signed in during the last 30 days, so there is no monthly audience to compare today against.'
                        : `${formatCount(stats.dau)} of the ${formatCount(stats.mau)} members who signed in this month came back today.`,
                  }
                : { absent: 'The pilot health read has not come back yet.' }),
              tooltip:
                'Distinct people with a sign-in session in the last 24 hours. This counts sessions, not generations.',
            },
            {
              id: 'outfits',
              label: 'Daily looks due in the window',
              ...(stats
                ? {
                    value: formatCount(stats.day_ahead_slots_in_window),
                    unit: plural(stats.day_ahead_slots_in_window, 'look'),
                    raw: stats.day_ahead_slots_in_window,
                  }
                : { absent: 'The pilot health read has not come back yet.' }),
              hint: `Daily looks made for a day in the last ${formatCount(stats?.window_days ?? 7)} days, reviewed or not.`,
              tooltip: DAILY_LOOKS_TOOLTIP,
            },
            {
              id: 'failures',
              label: 'Generation jobs that failed',
              ...(!stats
                ? { absent: 'The pilot health read has not come back yet.' }
                : jobs === 0
                  ? {
                      absent:
                        'No generation job ran in this window, so there is no denominator to take a failure share of. The read succeeded.',
                    }
                  : {
                      value: formatPercent(failed / jobs),
                      raw: `${failed} of ${jobs}`,
                      ...(failed > 0
                        ? { tone: 'error' as const, statusLabel: 'jobs failing' }
                        : {}),
                      bar: {
                        fraction: failed / jobs,
                        label: `Generation jobs that failed: ${failed} of ${jobs}`,
                        tone: 'error' as const,
                      },
                      hint: `${formatCount(failed)} of ${formatCount(jobs)} attempts. A job is one attempt at producing something, which is not one outfit.`,
                    }),
            },
            {
              id: 'coverage',
              label: 'Daily looks a stylist approved',
              ...(!stats
                ? { absent: 'The pilot health read has not come back yet.' }
                : stats.approval_coverage === null
                  ? {
                      absent:
                        'No daily look was due in this window, so there is nothing for a stylist to have approved yet.',
                    }
                  : {
                      value: formatPercent(stats.approval_coverage),
                      raw: stats.approval_coverage,
                      bar: {
                        fraction: stats.approval_coverage,
                        label: `Daily looks a stylist approved: ${stats.day_ahead_slots_approved_in_window} of ${stats.day_ahead_slots_in_window}`,
                        tone: 'success' as const,
                      },
                      hint: `${formatCount(stats.day_ahead_slots_approved_in_window)} of ${formatCount(stats.day_ahead_slots_in_window)} daily looks approved. A look nobody reviews still ships to the member at the 11 PM IST cutoff.`,
                    }),
            },
          ]}
        />
      }
    >
      <PilotHealth onLoaded={onLoaded} />
    </Page>
  );
}
